// ─────────────────────────────────────────────────────────────────────────
// templates.ts — HTML string builders for the element's Shadow DOM. Pure: take
// config/state, return markup. Wiring + recompute live in solar-calculator.ts.
// ─────────────────────────────────────────────────────────────────────────

import type { CalcResult, SystemResult } from '../../core/types.js';
import type { SolarCalcConfig } from '../../config/schema.js';
import { billChart, cumulativeChart, splitChart } from '../charts.js';
import { esc, fmtKwh, fmtMoney, fmtPct, fmtYears, fmtYearsLong } from './format.js';

const PROFILES: Array<{ v: string; l: string }> = [
  { v: 'mostly-daytime', l: 'Mostly during the day' },
  { v: 'mixed', l: 'A mix of day and evening' },
  { v: 'mostly-evenings', l: 'Mostly evenings' },
  { v: 'pool-or-ev', l: 'Heavy use (pool / EV)' },
];

const PERIODS: Array<{ v: string; l: string }> = [
  { v: 'weekly', l: 'per week' },
  { v: 'monthly', l: 'per month' },
  { v: 'quarterly', l: 'per quarter' },
  { v: 'annual', l: 'per year' },
];

/** Reusable info-icon tooltip — pure CSS via <details>, no JS, keyboard-accessible. */
export function tip(text: string): string {
  return `<details class="sc-tip"><summary aria-label="More information">i</summary><span class="sc-tip-body" role="tooltip">${esc(text)}</span></details>`;
}

const MODE_ICON: Record<string, string> = {
  residential:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>',
  commercial:
    '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><rect x="4" y="2" width="16" height="20" rx="2"/><path d="M9 22V12h6v10"/><path d="M8 6h.01M16 6h.01M12 6h.01M8 10h.01M16 10h.01M12 10h.01"/></svg>',
};

/** Residential / Commercial full-width segmented control (audience === 'both'). */
export function modeTabsHtml(config: SolarCalcConfig, active: string): string {
  return `
<div class="sc-segment" role="group" aria-label="Property type">
  ${(['residential', 'commercial'] as const)
    .map(
      (m) =>
        `<button type="button" data-sc-mode="${m}" aria-pressed="${active === m}">${MODE_ICON[m]}${esc(config.modes[m].label)}</button>`,
    )
    .join('')}
</div>`.trim();
}

/** The full input form: Quick fields + an Advanced drawer. */
export function inputsHtml(config: SolarCalcConfig): string {
  const d = config.defaults;
  const a = config.assumptions;
  const orientSel = config.orientationOptions.find((o) => o.factor === a.orientationFactor)?.value
    ?? config.orientationOptions[0]?.value ?? 'N';
  const shadeSel = config.shadeOptions.find((o) => o.factor === a.shadeFactor)?.value
    ?? config.shadeOptions[0]?.value ?? 'none';
  return `
<form class="sc-form" data-sc-form novalidate>
  <div class="sc-fields">
    <div class="sc-field">
      <label class="sc-label" for="sc-postcode">Postcode</label>
      <input class="sc-input" id="sc-postcode" data-sc="postcode" inputmode="numeric" maxlength="4" placeholder="e.g. 2000" autocomplete="postal-code"/>
      <span class="sc-hint" data-sc-region></span>
    </div>
    <div class="sc-field">
      <label class="sc-label" for="sc-bill" data-sc-billlabel>Electricity bill</label>
      <div class="sc-bill-row">
        <input class="sc-input" id="sc-bill" data-sc="bill" type="number" min="0" step="10" placeholder="400" value="400"/>
        <select class="sc-select" data-sc="billPeriod" aria-label="Bill period">
          ${PERIODS.map((p) => `<option value="${p.v}"${p.v === d.billPeriod ? ' selected' : ''}>${p.l}</option>`).join('')}
        </select>
      </div>
      <input class="sc-range sc-billrange" id="sc-bill-range" data-sc-billrange type="range" min="0" max="1500" step="25" value="400" aria-label="Bill amount slider"/>
      <span class="sc-hint" data-sc-billhint>Drag to adjust — or type an exact (or commercial) amount above.</span>
    </div>
    <div class="sc-field sc-field--full">
      <label class="sc-label" for="sc-profile" data-sc-profilelabel>When do you mostly use power?</label>
      <select class="sc-select" id="sc-profile" data-sc="usageProfile">
        ${PROFILES.map((p) => `<option value="${p.v}"${p.v === d.usageProfile ? ' selected' : ''}>${p.l}</option>`).join('')}
      </select>
    </div>
    <div class="sc-field sc-field--full">
      <div class="sc-row-inline">
        <label class="sc-check"><input type="checkbox" data-sc="hasExistingSolar"/> I already have solar</label>
        <label class="sc-check"><input type="checkbox" data-sc="batteryInterest"/> Include a battery</label>
      </div>
    </div>
  </div>

  <details class="sc-advanced">
    <summary>${CHEVRON} Advanced assumptions (editable)</summary>
    <div class="sc-fields">
      <div class="sc-field">
        <label class="sc-label" for="sc-size">System size <span class="sc-slider-val" data-sc-sizeval>${d.sizeKw} kW</span></label>
        <input id="sc-size" class="sc-range" data-sc="sizeKw" type="range" min="3" max="20" step="0.1" value="${d.sizeKw}"/>
      </div>
      <div class="sc-field">
        <div class="sc-label-row"><label class="sc-label" for="sc-orient">Roof orientation</label>${tip(config.tooltips.orientation)}</div>
        <select class="sc-select" id="sc-orient" data-sc="orientation">
          ${config.orientationOptions.map((o) => `<option value="${o.value}"${o.value === orientSel ? ' selected' : ''}>${esc(o.label)} (${o.lossPct}% loss)</option>`).join('')}
        </select>
      </div>
      <div class="sc-field">
        <div class="sc-label-row"><label class="sc-label" for="sc-shade">Shade</label>${tip(config.tooltips.shade)}</div>
        <select class="sc-select" id="sc-shade" data-sc="shade">
          ${config.shadeOptions.map((o) => `<option value="${o.value}"${o.value === shadeSel ? ' selected' : ''}>${esc(o.label)} (${o.lossPct}% loss)</option>`).join('')}
        </select>
      </div>
      <div class="sc-field">
        <label class="sc-label" for="sc-rate">Retail rate ($/kWh)</label>
        <input class="sc-input" id="sc-rate" data-sc="retailRate" type="number" step="0.01" min="0"/>
      </div>
      <div class="sc-field">
        <label class="sc-label" for="sc-fit">Feed-in tariff ($/kWh)</label>
        <input class="sc-input" id="sc-fit" data-sc="feedInTariff" type="number" step="0.01" min="0"/>
      </div>
      <div class="sc-field">
        <label class="sc-label" for="sc-supply">Supply charge ($/day)</label>
        <input class="sc-input" id="sc-supply" data-sc="supplyChargePerDay" type="number" step="0.01" min="0"/>
      </div>
      <div class="sc-field">
        <label class="sc-label" for="sc-batkwh">Battery size (kWh)</label>
        <input class="sc-input" id="sc-batkwh" data-sc="batteryUsableKwh" type="number" step="0.5" min="0" value="${d.battery.defaultUsableKwh}"/>
      </div>
      <div class="sc-field">
        <label class="sc-label" for="sc-cost" data-sc-costlabel>Solar cost ($/kW)</label>
        <input class="sc-input" id="sc-cost" data-sc="costPerKw" type="number" step="50" min="0" value="${a.costPerKw}"/>
      </div>
    </div>
  </details>
</form>`.trim();
}

/** Chevron used in collapsible <summary> rows (rotates 90° via CSS when open). */
const CHEVRON =
  '<svg class="sc-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true"><path d="M6 4l4 4-4 4"/></svg>';

function card(label: string, value: string, sub = '', positive = false, hero = false): string {
  return `
<div class="sc-card${hero ? ' sc-card--hero' : ''}">
  <p class="sc-card-label">${label}</p>
  <p class="sc-card-value${positive ? ' sc-card-value--positive' : ''}">${value}</p>
  ${sub ? `<p class="sc-card-sub">${sub}</p>` : ''}
</div>`;
}

/** 1 / 5 / 10 / 25-year bill-with/without/savings milestone table (brief §10a gap I). */
function milestoneTable(sys: SystemResult, inflation: number): string {
  const billYear1 = sys.newAnnualBill + sys.year1Savings; // pre-solar, year 1
  const rows = [1, 5, 10, 25].map((y) => {
    const without = billYear1 * (1 + inflation) ** (y - 1);
    const saving = sys.lifetime[y - 1]?.savings ?? 0;
    const withSolar = Math.max(0, without - saving);
    return `<tr><td>${y} yr</td><td>${fmtMoney(without)}</td><td>${fmtMoney(withSolar)}</td><td class="sc-pos">${fmtMoney(saving)}</td></tr>`;
  });
  return `
<div class="sc-milestones">
  <h4>Your bill over time</h4>
  <table class="sc-mtable">
    <thead><tr><th>Year</th><th>Without solar</th><th>With solar</th><th>Saved</th></tr></thead>
    <tbody>${rows.join('')}</tbody>
  </table>
</div>`;
}

/** The results block: cards, charts, battery-marginal, sensitivity, fine print, CTA. */
export function resultsHtml(
  config: SolarCalcConfig,
  result: CalcResult,
  view: 'solar' | 'battery',
  asOf: string,
  mode: { gstRegistered: boolean; note?: string },
): string {
  const hasBattery = result.solarPlusBattery != null;
  const sys =
    view === 'battery' && result.solarPlusBattery
      ? result.solarPlusBattery
      : result.solar;

  const toggle = hasBattery
    ? `<div class="sc-toggle" role="group" aria-label="Compare systems">
         <button type="button" data-sc-view="solar" aria-pressed="${view === 'solar'}">Solar only</button>
         <button type="button" data-sc-view="battery" aria-pressed="${view === 'battery'}">Solar + battery</button>
       </div>`
    : '';

  const batteryNote =
    hasBattery && result.batteryMarginal
      ? `<div class="sc-battery-note">
           <b>Battery, costed separately.</b> Adding the battery costs about
           <b>${fmtMoney(result.batteryMarginal.extraUpfront)}</b> more upfront and saves about
           <b>${fmtMoney(result.batteryMarginal.extraAnnualSaving)}</b> more a year — a marginal payback of
           <b>${fmtYears(result.batteryMarginal.marginalPaybackYears)}</b> on the battery alone.
           We never blend this into the solar payback.
         </div>`
      : '';

  const s = result.paybackSensitivity;
  const sensitivity = `
    <p class="sc-range-line">Payback range: <b>${fmtYears(s.optimistic)}</b> optimistic ·
       <b>${fmtYears(s.expected)}</b> expected · <b>${fmtYears(s.conservative)}</b> conservative
       <span class="sc-chip">${esc(config.copy.zeroFitLabel)}: <b>${fmtYears(result.zeroFitPaybackYears)}</b></span></p>
    <p class="sc-note">${esc(config.copy.selfConsumptionNote)} ${tip(config.tooltips.selfConsumption)}</p>`;

  const sizingNote = result.oversizedForUsage
    ? `<p class="sc-note sc-note--sizing">${esc(config.copy.sizingNote)}</p>`
    : '';

  const r = sys.returns;
  const investBlock = `
    <div class="sc-invest">
      <span class="sc-invest-stat">ROI <b>${Math.round(r.roiPercent)}%</b>${tip(config.tooltips.roi)}</span>
      <span class="sc-invest-stat">IRR <b>${r.irr == null ? '—' : `${r.irr}%`}</b>${tip(config.tooltips.irr)}</span>
      <span class="sc-invest-stat">NPV <b>${fmtMoney(r.npv)}</b>${tip(config.tooltips.npv)}</span>
      <span class="sc-invest-stat">Panels <b>${sys.panelCount}</b><span class="sc-invest-sub">@ ${config.assumptions.panelWatts} W</span></span>
    </div>`;

  const actions = config.client.quoteUrl
    ? `<div class="sc-cta"><a class="sc-btn" href="${esc(config.client.quoteUrl)}">${esc(config.copy.ctaLabel)}</a></div>`
    : '';

  const gstNote = mode.gstRegistered
    ? `<p class="sc-note">${esc(config.copy.commercialGstNote)}</p>`
    : '';
  const modeNote = mode.note ? `<p class="sc-note sc-note--sizing">${esc(mode.note)}</p>` : '';

  return `
${toggle}
<div class="sc-cards">
  ${card('Payback', fmtYearsLong(sys.paybackYears), `${sys.sizeKw} kW system`, true, true)}
  ${card('System size', `${sys.sizeKw} kW`, sys.sizeKw === result.recommendedSizeKw ? `${sys.panelCount} panels, sized to your usage` : `${sys.panelCount} panels, we’d suggest ${result.recommendedSizeKw} kW`)}
  ${card('Cost after STC', fmtMoney(sys.netCost), `${fmtMoney(sys.grossCost)} less ${fmtMoney(sys.stcValue + sys.batteryRebate)} rebate`)}
  ${card('Annual saving', fmtMoney(sys.year1Savings), 'year one', true)}
  ${card('New annual bill', fmtMoney(sys.newAnnualBill), `was ${fmtMoney(sys.newAnnualBill + sys.year1Savings)}`)}
  ${card('25-year net', fmtMoney(sys.cumulativeNet25), `${fmtMoney(sys.cumulativeNet10)} at 10 yr`, sys.cumulativeNet25 > 0)}
</div>
${sizingNote}
${sensitivity}
${investBlock}
${batteryNote}
<div class="sc-charts">
  <div class="sc-chart"><h4>Annual bill</h4>${billChart(sys.newAnnualBill + sys.year1Savings, sys.newAnnualBill)}</div>
  <div class="sc-chart"><h4>Where your solar goes</h4>${splitChart(sys.split.selfKwh, sys.split.exportKwh)}
    <p class="sc-card-sub">${fmtKwh(sys.annualGenerationKwh)} generated · ${fmtPct(sys.split.selfFraction)} used at home · ${Math.round(sys.emissionsAvoidedKgPerYear)} kg CO₂ avoided/yr</p></div>
  <div class="sc-chart"><h4>25-year cumulative net</h4>${cumulativeChart(sys.lifetime, sys.paybackYears)}</div>
</div>
${milestoneTable(sys, config.assumptions.electricityInflation)}
${assumptionsPanelHtml(config, asOf)}
${gstNote}
${modeNote}
<div class="sc-disclaimer">${esc(config.copy.resultDisclaimer)}</div>
${actions}
${finePrintHtml(config, asOf)}`.trim();
}

/** Disclosed-assumptions panel (read-only) — the defaults driving the numbers. */
export function assumptionsPanelHtml(config: SolarCalcConfig, asOf: string): string {
  const rows = config.disclosures
    .map(
      (d) => `<tr><th scope="row">${esc(d.label)}</th><td><b>${esc(d.value)}</b><span class="sc-assum-src">${esc(d.source)}</span></td></tr>`,
    )
    .join('');
  return `
<details class="sc-assumptions">
  <summary>${CHEVRON} ${esc(config.copy.assumptionsHeading)}</summary>
  <table class="sc-assum-table"><tbody>${rows}</tbody></table>
  <p class="sc-assum-gst">${esc(config.copy.gstNote)}</p>
  <p class="sc-assum-asof">Assumptions current as of <span class="sc-asof">${esc(asOf)}</span>. Editable in Advanced.</p>
</details>`.trim();
}

/** Fine-print data-source attribution + "current as of" date (licence obligation). */
export function finePrintHtml(config: SolarCalcConfig, asOf: string): string {
  const sourcesIntro = config.copy.dataSourcesIntro.replace('{asOf}', asOf);
  return `
<div class="sc-fineprint">
  <p><b>About this estimate.</b> ${esc(config.copy.aboutEstimate)}</p>
  <p><b>Data sources.</b> ${esc(sourcesIntro).replace(/Assumptions current as of (.+?)\.$/, 'Assumptions current as of <span class="sc-asof">$1</span>.')}</p>
</div>`.trim();
}

const UTIL_ICON = {
  pdf: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>',
  link: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"/><path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"/></svg>',
  share: '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>',
};

/** The lead-capture form + save/share toolbar (demo or live per config.client.webhookUrl). */
export function leadFormHtml(config: SolarCalcConfig): string {
  return `
<div class="sc-lead">
  <div class="sc-lead-head">
    <div>
      <h3 data-sc-leadheading>${esc(config.copy.leadHeading)}</h3>
      <p class="sc-lead-sub">We’ll confirm your roof, switchboard &amp; tariff — no obligation.</p>
    </div>
    <div class="sc-utils">
      <button type="button" class="sc-util-btn" data-sc-pdf title="Save as PDF">${UTIL_ICON.pdf}<span class="sc-util-label">PDF</span></button>
      <button type="button" class="sc-util-btn" data-sc-copylink title="Copy link">${UTIL_ICON.link}<span class="sc-util-label">Link</span></button>
      <button type="button" class="sc-util-btn" data-sc-share title="${esc(config.copy.shareLabel)}">${UTIL_ICON.share}<span class="sc-util-label">Share</span></button>
    </div>
  </div>
  <form data-sc-lead novalidate>
    <div class="sc-fields">
      <div class="sc-field">
        <label class="sc-label" for="sc-name">Name</label>
        <input class="sc-input" id="sc-name" name="name" required placeholder="Your name"/>
      </div>
      <div class="sc-field">
        <label class="sc-label" for="sc-phone">Phone</label>
        <input class="sc-input" id="sc-phone" name="phone" type="tel" required placeholder="Mobile or landline"/>
      </div>
      <div class="sc-field">
        <label class="sc-label" for="sc-email">Email (optional)</label>
        <input class="sc-input" id="sc-email" name="email" type="email" placeholder="you@example.com"/>
      </div>
      <div class="sc-field">
        <label class="sc-label" for="sc-roof">Roof type</label>
        <select class="sc-select" id="sc-roof" name="roofType">
          <option value="">Select…</option>
          <option>Tile</option><option>Metal / Colorbond</option><option>Klip-Lok</option><option>Flat</option><option>Not sure</option>
        </select>
      </div>
      <div class="sc-field sc-field--full">
        <label class="sc-label" for="sc-address">${esc(config.copy.addressLabel)}</label>
        <input class="sc-input" id="sc-address" name="address" required placeholder="Street address for the site assessment"/>
      </div>
    </div>
    <p class="sc-lead-error sc-hidden" data-sc-lead-error role="alert">Please add your name, phone number and address.</p>
    <button class="sc-btn sc-btn--block" type="submit" style="margin-top:14px">Get my quote</button>
    <div class="sc-lead-success sc-hidden" data-sc-lead-success></div>
  </form>
</div>`.trim();
}
